// ADR-199: Reports → Integrity Checks.
//
// Runs radar-api's read-only integrity checks (ADR-153 step B) live when the
// page opens and on "Run again": the employment and contract rules that the
// application enforces and the database does not, and manager history against
// the organization structure history. Nothing is stored or changed. This page
// replaced the daily scheduled run, whose only output was a log line; the
// scheduler job is now manual-only.
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSyncAlt } from 'react-icons/fa';
import { Alert, Typography } from '@mui/material';
import {
  Container, Header, HeaderLeft, Title, Content, MainSection, BackButton,
} from '../styles';
import { HeaderButton } from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import { useIntegrityChecks } from '../../../hooks/useIntegrityChecks';
import {
  INTEGRITY_SCOPE_LABELS, INTEGRITY_SCOPE_DESCRIPTIONS, INTEGRITY_SCOPES,
} from '../../../constants/integrityChecks';
import { sortResults, summarizeScopes, describeContractLimits } from '../../../utils/integrityChecks';
import {
  SummaryGrid, SummaryTile, SummaryLabel, SummaryValue, SummaryMeta,
} from '../DirectManagerDiscrepancies/styles';
import CheckCard from './CheckCard';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ScopeSection = ({ scope, report, error, onOpenEmployee }) => (
  <section style={{ marginTop: 24 }}>
    <Typography variant="h6">{INTEGRITY_SCOPE_LABELS[scope]}</Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
      {INTEGRITY_SCOPE_DESCRIPTIONS[scope]}
    </Typography>
    {error && (
      <Alert severity="error">{`These checks could not run: ${error?.message || 'unknown error'}.`}</Alert>
    )}
    {report && (
      <>
        <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 1 }}>
          {`Ran ${formatDateTime(report.ranAt)} · ${report.results?.length || 0} checks`}
          {scope === INTEGRITY_SCOPES.EMPLOYMENT && report.openContractLimits
            && ` · Open-contract limits: ${describeContractLimits(report.openContractLimits)}`}
        </Typography>
        {sortResults(report.results).map((result) => (
          <CheckCard key={result.check} result={result} onOpenEmployee={onOpenEmployee} />
        ))}
      </>
    )}
  </section>
);

const IntegrityChecks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const openIndividual = useOpenIndividual();
  const {
    reports, errors, isLoading, isRunning, rerun, scopes,
  } = useIntegrityChecks();

  const summary = summarizeScopes(reports);
  const anyError = scopes.some((s) => errors[s]);

  const handleOpenEmployee = (employeeId) => {
    openIndividual(employeeId, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.INTEGRITY_CHECKS,
      tab: 'EMPLOYMENT_EPISODES',
      returnTo: location.pathname,
      returnLabel: 'Integrity Checks',
    });
  };

  let statusValue = '—';
  let statusColor;
  if (summary.loadedScopes > 0) {
    statusValue = summary.healthy && !anyError ? 'Healthy' : `${summary.totalViolations} violations`;
    statusColor = summary.totalViolations > 0 ? '#991B1B' : (anyError ? undefined : '#1E8E3E');
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Integrity Checks</Title>
        </HeaderLeft>
        <HeaderButton onClick={rerun} disabled={isLoading || isRunning} title="Run every check again now">
          <FaSyncAlt />
          {isRunning ? 'Running…' : 'Run again'}
        </HeaderButton>
      </Header>

      <Content>
        <MainSection>
          {isLoading && <LoadingSpinner />}

          {!isLoading && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                The checks run live when this page opens. They only read data; nothing is stored or changed.
                A clean system shows every check as passed.
              </Alert>

              <SummaryGrid>
                <SummaryTile>
                  <SummaryLabel>Status</SummaryLabel>
                  <SummaryValue style={statusColor ? { color: statusColor } : undefined}>{statusValue}</SummaryValue>
                  <SummaryMeta>{anyError ? 'Some checks could not run' : `${summary.totalChecks} checks ran`}</SummaryMeta>
                </SummaryTile>
                <SummaryTile>
                  <SummaryLabel>Failing checks</SummaryLabel>
                  <SummaryValue style={summary.failingChecks > 0 ? { color: '#991B1B' } : undefined}>{summary.failingChecks}</SummaryValue>
                  <SummaryMeta>{`of ${summary.totalChecks}`}</SummaryMeta>
                </SummaryTile>
                <SummaryTile>
                  <SummaryLabel>Informational</SummaryLabel>
                  <SummaryValue>{summary.informational}</SummaryValue>
                  <SummaryMeta>Allowed by Region settings; not violations</SummaryMeta>
                </SummaryTile>
              </SummaryGrid>

              {scopes.map((scope) => (
                <ScopeSection
                  key={scope}
                  scope={scope}
                  report={reports[scope]}
                  error={errors[scope]}
                  onOpenEmployee={handleOpenEmployee}
                />
              ))}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default IntegrityChecks;
